import React, { useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { makeStyles } from "@material-ui/core/styles";
import API from "utils/api";
import styles from "./productPageStyle.js";

const useStyles = makeStyles(styles);

const MESSAGE_MIN = 10;

/**
 * B-01 §28.3 item 6, without a login.
 *
 * The only product context sent is the authId — cosahh-rest resolves the name
 * and manufacturer itself, so a caller cannot attach their words to a product
 * they did not scan. The `website` field is a honeypot: it is off-screen, so a
 * human never fills it and a form-filling bot does; the backend drops any
 * submission that carries a value.
 */
export default function ComplaintDialog({ t, open, onClose, productAuthId }) {
  const classes = useStyles();
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  // Guards against setting state from a response that lands after the page
  // has navigated away — the dialog itself only hides (open=false), it does
  // not unmount, but ProductPage as a whole can.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const canSubmit = message.trim().length >= MESSAGE_MIN && !sending;

  const handleClose = () => {
    onClose();
    // The error must never survive a cancel-and-reopen: a stale "could not be
    // sent" would greet the reporter before they have done anything this
    // time. The typed text, however, is only reset after a successful send —
    // a failed attempt keeps it so the reporter does not have to retype it.
    setError(null);
    if (sent) {
      setSent(false);
      setMessage("");
      setEmail("");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    setError(null);

    API.post("/feedback/add", {
      category: "ULTRAGREEN_COMPLAINT",
      message: message.trim(),
      productAuthId: productAuthId,
      userEmail: email.trim(),
      website: website,
    })
      .then(() => {
        if (!mountedRef.current) return;
        setSent(true);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        const rateLimited = err.response && err.response.status === 429;
        setError(rateLimited ? t.dialogErrorRateLimit : t.dialogErrorGeneric);
      })
      .finally(() => {
        if (!mountedRef.current) return;
        setSending(false);
      });
  };

  if (sent) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle disableTypography>
          <h2 className={classes.dialogTitle}>{t.dialogSentTitle}</h2>
        </DialogTitle>
        <DialogContent>
          <p className={classes.dialogIntro}>{t.dialogSentBody}</p>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className={classes.reportButton}
            onClick={handleClose}
          >
            {t.dialogClose}
          </button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography>
        <h2 className={classes.dialogTitle}>{t.dialogTitle}</h2>
      </DialogTitle>
      <form id="ultragreen-complaint-form" onSubmit={handleSubmit}>
        <DialogContent>
          <p className={classes.dialogIntro}>{t.dialogIntro}</p>

          <label className={classes.dialogLabel} htmlFor="ug-complaint-message">
            {t.dialogMessageLabel}
          </label>
          <textarea
            id="ug-complaint-message"
            className={classes.dialogField}
            rows={5}
            value={message}
            placeholder={t.dialogMessagePlaceholder}
            onChange={(e) => setMessage(e.target.value)}
          />

          <label className={classes.dialogLabel} htmlFor="ug-complaint-email">
            {t.dialogEmailLabel}
          </label>
          <input
            id="ug-complaint-email"
            type="email"
            className={classes.dialogField}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <p className={classes.dialogHelp}>{t.dialogEmailHelp}</p>

          <div className={classes.dialogHoneypot} aria-hidden="true">
            {/* id/label deliberately avoid "website"/"url" wording: password
                managers and browser autofill target hidden fields by id and
                label text, and "Website" is exactly what they look for. The
                wire field name stays `website` — that is the backend
                contract — only the DOM-visible id/label/autocomplete change. */}
            <label htmlFor="ug-complaint-hp">Leave this field empty</label>
            <input
              id="ug-complaint-hp"
              type="text"
              tabIndex={-1}
              autoComplete="new-password"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          {error && (
            <p className={classes.dialogError}>
              {error}{" "}
              <a href={"mailto:" + t.contactEmail}>{t.contactEmail}</a>
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className={classes.dialogCancelButton}
            onClick={handleClose}
          >
            {t.dialogCancel}
          </button>
          <button
            type="submit"
            className={classes.reportButton}
            disabled={!canSubmit}
          >
            {sending ? t.dialogSending : t.dialogSubmit}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
